import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { InsertDto, UpdateDto } from "@stockuj/types";

export function useLocations(organizationId: string | undefined) {
  return useQuery({
    queryKey: ["locations", organizationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("locations")
        .select("*")
        .eq("organization_id", organizationId!)
        .eq("is_active", true)
        .order("is_primary", { ascending: false })
        .order("name");

      if (error) throw error;
      return data;
    },
    enabled: !!organizationId,
  });
}

export function useCreateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (location: InsertDto<"locations">) => {
      const { data, error } = await supabase.from("locations").insert(location).select().single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["locations", variables.organization_id],
      });
      queryClient.invalidateQueries({ queryKey: ["org-usage"] });
    },
  });
}

export function useUpdateLocation(organizationId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      locationId,
      updates,
    }: {
      locationId: string;
      updates: UpdateDto<"locations">;
    }) => {
      const { data, error } = await supabase
        .from("locations")
        .update(updates)
        .eq("id", locationId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["locations", organizationId],
      });
    },
  });
}

export function useDeleteLocation(organizationId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (locationId: string) => {
      const { error } = await supabase.from("locations").delete().eq("id", locationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["locations", organizationId],
      });
      queryClient.invalidateQueries({ queryKey: ["org-usage"] });
    },
  });
}
